/**
 * Typed HTTP client for the BEAM platform-admin surface (`/api/admin`). Deliberately SEPARATE from
 * src/api/client.ts so admin and tenant credentials never share an accessor:
 *  - Its own in-memory access token + CSRF accessors (injected by src/auth/adminSession.ts).
 *  - Always sends the httpOnly `beam_admin_refresh` cookie (credentials:'include') — web-only console.
 *  - Echoes X-CSRF-Token on the state-changing auth routes (/auth/refresh, /auth/logout).
 *  - One-time refresh-and-retry on 401 (rotates the admin refresh token).
 * All shapes come from @beam/api-types.
 */
import {
  AdminLoginResponse,
  AdminTokenResponse,
  AdminMfaEnrollResponse,
  AdminMeResponse,
  OrgListResponse,
  OrgDetail,
  LicenseActionResponse,
  IssueCompedResponse,
  CheckoutLinkResponse,
  AdminAuditRow,
  DashboardResponse,
  type IssueCompedRequest,
  type CheckoutLinkRequest,
} from '@beam/api-types';
import { z } from 'zod';
import { API_BASE_URL } from '../config';

let _getToken: (() => string | null) | null = null;
let _getCsrf: (() => string | null) | null = null;
let _refresh: (() => Promise<string | null>) | null = null;

export function setAdminTokenAccessor(fn: () => string | null): void {
  _getToken = fn;
}
export function setAdminCsrfAccessor(fn: () => string | null): void {
  _getCsrf = fn;
}
export function setAdminRefreshHandler(fn: () => Promise<string | null>): void {
  _refresh = fn;
}

export class AdminApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'AdminApiError';
  }
}

const CSRF_ROUTES = new Set(['/api/admin/auth/refresh', '/api/admin/auth/logout']);

async function request<T>(method: 'GET' | 'POST' | 'DELETE', path: string, body?: unknown, retried = false): Promise<T> {
  const token = _getToken?.() ?? null;
  const headers: Record<string, string> = { 'Content-Type': 'application/json', Accept: 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (CSRF_ROUTES.has(path)) headers['X-CSRF-Token'] = _getCsrf?.() ?? '';

  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'include', // send the httpOnly admin refresh cookie
  });

  const text = await res.text();
  let json: unknown;
  try {
    json = text ? JSON.parse(text) : {};
  } catch {
    throw new AdminApiError('parse_error', `Non-JSON response from ${method} ${path}`, res.status);
  }

  if (!res.ok) {
    // One-time refresh-and-retry on an expired admin token — but ONLY for business routes. A 401 from
    // the auth endpoints (login/verify-mfa/confirm-enroll/enroll) means "bad credentials / wrong code",
    // NOT an expired token; refreshing there would wrongly clear a valid pre-MFA session and bounce the
    // user to login instead of surfacing the error.
    if (res.status === 401 && token && !retried && _refresh && !path.startsWith('/api/admin/auth/')) {
      const fresh = await _refresh();
      if (fresh) return request<T>(method, path, body, true);
    }
    const env = json as { error?: { code?: string; message?: string } };
    throw new AdminApiError(env.error?.code ?? 'error', env.error?.message ?? `HTTP ${res.status}`, res.status);
  }
  return json as T;
}

// --- Auth ---
export async function adminLoginRequest(email: string, password: string): Promise<AdminLoginResponse> {
  return AdminLoginResponse.parse(await request('POST', '/api/admin/auth/login', { email, password }));
}
export async function adminVerifyMfaRequest(code: string): Promise<AdminTokenResponse> {
  return AdminTokenResponse.parse(await request('POST', '/api/admin/auth/verify-mfa', { code }));
}
export async function adminEnrollRequest(): Promise<AdminMfaEnrollResponse> {
  return AdminMfaEnrollResponse.parse(await request('POST', '/api/admin/auth/enroll', {}));
}
export async function adminConfirmEnrollRequest(code: string): Promise<AdminTokenResponse> {
  return AdminTokenResponse.parse(await request('POST', '/api/admin/auth/confirm-enroll', { code }));
}
const AdminRefreshResponse = z.object({ token: z.string(), expiresInSeconds: z.number(), csrfToken: z.string(), mfaSatisfied: z.boolean() });
export async function adminRefreshRequest(): Promise<z.infer<typeof AdminRefreshResponse>> {
  return AdminRefreshResponse.parse(await request('POST', '/api/admin/auth/refresh', {}));
}
export async function adminLogoutRequest(): Promise<void> {
  await request('POST', '/api/admin/auth/logout', {}).catch(() => undefined);
}
export async function adminMeRequest(): Promise<AdminMeResponse> {
  return AdminMeResponse.parse(await request('GET', '/api/admin/auth/me'));
}

// --- Business ---
export async function adminListOrgs(params: { search?: string; limit?: number; offset?: number } = {}): Promise<OrgListResponse> {
  const q = new URLSearchParams();
  if (params.search) q.set('search', params.search);
  if (params.limit != null) q.set('limit', String(params.limit));
  if (params.offset != null) q.set('offset', String(params.offset));
  const qs = q.toString();
  return OrgListResponse.parse(await request('GET', `/api/admin/orgs${qs ? `?${qs}` : ''}`));
}
export async function adminGetOrg(id: string): Promise<OrgDetail> {
  return OrgDetail.parse(await request('GET', `/api/admin/orgs/${id}`));
}
export async function adminIssueComped(body: IssueCompedRequest): Promise<IssueCompedResponse> {
  return IssueCompedResponse.parse(await request('POST', '/api/admin/orgs', body));
}
export async function adminCheckoutLink(body: CheckoutLinkRequest): Promise<CheckoutLinkResponse> {
  return CheckoutLinkResponse.parse(await request('POST', '/api/admin/licenses/checkout-link', body));
}
export async function adminLicenseAction(licenseId: string, action: 'suspend' | 'revoke' | 'renew'): Promise<LicenseActionResponse> {
  return LicenseActionResponse.parse(await request('POST', `/api/admin/licenses/${licenseId}/${action}`, {}));
}
export async function adminGetDashboard(): Promise<DashboardResponse> {
  return DashboardResponse.parse(await request('GET', '/api/admin/dashboard'));
}
export async function adminListAudit(params: { orgId?: string; action?: string; limit?: number } = {}): Promise<AdminAuditRow[]> {
  const q = new URLSearchParams();
  if (params.orgId) q.set('orgId', params.orgId);
  if (params.action) q.set('action', params.action);
  if (params.limit != null) q.set('limit', String(params.limit));
  const qs = q.toString();
  return z.array(AdminAuditRow).parse(await request('GET', `/api/admin/audit${qs ? `?${qs}` : ''}`));
}
